import { useEffect, useMemo, useState } from 'react'
import { normalizedText } from '@domain/product'
import type { Product, ProductFamily } from '@domain/product'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { PilhaDeFotos } from './PilhaDeFotos'
import { Miniatura } from './Miniatura'
import { EscolhaEmTelaCheia } from '@ui/shell/EscolhaEmTelaCheia'

type Props = {
  /** A família atual do produto, para marcar a escolhida. */
  atual: string | null
  familias: ProductFamily[]
  onEscolher: (familyId: string | null) => void
  /**
   * Criar uma família com este nome e entrar nela.
   *
   * Separado do `onEscolher` porque o que viaja aqui é um **nome**, e lá é um
   * **id** — as duas são `string`, e mandar as duas pela mesma porta seria um
   * tipo que compila e uma confusão que não aparece até alguém criar uma
   * família chamada como o id de outra.
   */
  onCriar: (nome: string) => void
  onFechar: () => void
}

/**
 * Escolher a família de um produto, em tela cheia.
 *
 * ## O `<select>` nativo funcionava com três famílias
 *
 * Com trinta ele quebra: não dá para buscar, a lista do sistema não diz
 * quantos tamanhos cada família tem, e criar uma exigia fechar o seletor e
 * achar um link "Criar família" ao lado dele. Escolher onde um produto entra é
 * uma decisão sobre o catálogo inteiro da casa — merece a tela inteira.
 *
 * ## Com as fotos, porque é assim que se reconhece uma embalagem
 *
 * É o mesmo argumento que criou o baralho na lista de grupos (decisão 226):
 * "Coca-Cola" é só uma palavra, e o baralho mostra **como as embalagens são**.
 * Numa lista de escolha isso vale ainda mais — a pergunta é *qual destas*.
 *
 * O preço é altura: cabem cinco famílias por tela onde uma lista de texto puro
 * mostraria nove. Aceito de propósito; a busca resolve a lista longa, e
 * reconhecer resolve a curta.
 *
 * ## "Criar" mora no rodapé FIXO, e não como primeira linha da lista
 *
 * Como primeira linha — que é o padrão da busca de Lembretes — ele sobe junto
 * com os resultados: numa casa com oito famílias parecidas, criar sai do
 * alcance do polegar exatamente quando a busca não achou nada, que é quando
 * ele é a saída. No rodapé ele não foge.
 *
 * Com o campo vazio o botão **foca o campo** em vez de criar: não há nome para
 * dar à família. Desabilitá-lo diria a mesma coisa e deixaria a pessoa tocando
 * num alvo morto.
 *
 * ## Ela vai para o `<body>` por PORTAL, e isso não é preferência
 *
 * `position: fixed` não é relativo à janela quando um ancestral tem
 * `filter`, `transform` ou **`backdrop-filter`** — esse ancestral vira o bloco
 * de contenção. O gatilho mora dentro do cartão de vidro do cabeçalho, que é
 * um `Glass level="frost"` com `backdrop-filter: blur(18px)`: sem o portal, o
 * `inset-0` media o CARTÃO, e a "tela cheia" abria como uma tira de 150px
 * presa ao cabeçalho. Medido no navegador.
 *
 * A `FolhaDeBaixo` não precisa disto por acidente — ela é renderizada no nível
 * da página, longe de qualquer vidro. Quem abre painel de dentro de um cartão
 * precisa do portal.
 *
 * ## A lista de produtos é lida ao ABRIR, e não pela página
 *
 * A contagem de tamanhos e o baralho saem dos produtos de cada família, e a
 * página do produto não tem esse dado — ela conhece um produto só. Ler aqui
 * custa uma consulta por abertura de um painel que se abre de propósito, e
 * evita que toda visita à página do produto pague por ela.
 */
export function EscolherFamilia({ atual, familias, onEscolher, onCriar, onFechar }: Props) {
  const { productRepository } = useFinanceRepositories()
  const [busca, setBusca] = useState('')
  const [produtos, setProdutos] = useState<Product[]>([])

  useEffect(() => {
    let vivo = true
    void productRepository
      .list()
      .then((todos) => {
        if (vivo) setProdutos(todos)
      })
      .catch((e: unknown) => {
        // A escolha funciona sem as fotos: sem elas as linhas ficam com o nome
        // e nada mais, que ainda responde a pergunta.
        console.error('EscolherFamilia: os produtos não carregaram', e)
      })
    return () => {
      vivo = false
    }
  }, [productRepository])

  const porFamilia = useMemo(() => {
    const mapa = new Map<string, Product[]>()
    for (const p of produtos) {
      if (p.familyId === null || p.archivedAt !== null) continue
      mapa.set(p.familyId, [...(mapa.get(p.familyId) ?? []), p])
    }
    return mapa
  }, [produtos])

  // A MESMA normalização do catálogo e da busca de lembretes: "cafe" acha
  // "Café", e sem isso o acento decidiria se a família aparece.
  const termo = normalizedText(busca)
  const visiveis = familias
    .filter((f) => f.archivedAt === null || f.id === atual)
    .filter((f) => termo === '' || normalizedText(f.name).includes(termo))

  const nomeNovo = busca.trim()
  const jaExiste = familias.some((f) => normalizedText(f.name) === termo)

  return (
    <EscolhaEmTelaCheia
      titulo="Família"
      rotulo="Escolher a família"
      rotuloDaBusca="Buscar família"
      busca={busca}
      onBuscar={setBusca}
      onFechar={onFechar}
      criar={{
        rotulo: nomeNovo === '' || jaExiste ? '+ Criar uma família' : `+ Criar “${nomeNovo}”`,
        // `null` com o campo vazio ou o nome já existindo: nos dois casos não
        // há família nova a criar, e o toque leva de volta à busca.
        aoCriar:
          nomeNovo === '' || jaExiste
            ? null
            : () => {
                onCriar(nomeNovo)
              },
      }}
    >
      <ul aria-label="Famílias">
        {/* "Sem família" é uma escolha, e não a ausência de uma: produto solto
            entra na lista de grupos como grupo de um (decisão 202). Por isso
            ela é uma linha como as outras, e não um "limpar". */}
        {termo === '' && (
          <li>
            <LinhaDeFamilia
              nome="Sem família"
              // O traço no lugar da inicial: um "S" ali leria como se "Sem
              // família" fosse uma família chamada assim.
              inicial="—"
              detalhe="comparado sozinho"
              escolhida={atual === null}
              onClick={() => {
                onEscolher(null)
              }}
            />
          </li>
        )}

        {visiveis.map((f) => (
          <li key={f.id}>
            <LinhaDeFamilia
              nome={f.name}
              variantes={porFamilia.get(f.id) ?? []}
              escolhida={atual === f.id}
              onClick={() => {
                onEscolher(f.id)
              }}
            />
          </li>
        ))}

        {visiveis.length === 0 && (
          <li className="px-1 py-6 text-center text-sm text-[var(--text-muted)]">
            {`Nenhuma família com “${nomeNovo}”. Dá para criar uma abaixo.`}
          </li>
        )}
      </ul>
    </EscolhaEmTelaCheia>
  )
}

/** Uma família na lista: as fotos, o nome e quantos tamanhos ela tem. */
function LinhaDeFamilia({
  nome,
  detalhe,
  inicial,
  variantes = [],
  escolhida,
  onClick,
}: {
  nome: string
  detalhe?: string
  /** O que aparece na moldura sem foto. Por padrão, a inicial do nome. */
  inicial?: string
  variantes?: Product[]
  escolhida: boolean
  onClick: () => void
}) {
  // "1 tamanho" informa; "0 tamanhos" seria ruído numa família recém-criada.
  const quantos = variantes.length
  const contagem =
    quantos === 0 ? null : quantos === 1 ? '1 tamanho' : `${String(quantos)} tamanhos`

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={escolhida ? 'true' : undefined}
      // O véu da escolhida é da MARCA, e não branco: branco sobre este fundo
      // vira cinza sujo. A borda de baixo some na escolhida para o retângulo
      // tintado não parecer cortado ao meio.
      className={`alvo w-full justify-start gap-3 px-2 text-left text-sm ${
        escolhida
          ? 'rounded-[var(--radius-sm)] bg-[color-mix(in_oklab,var(--brand)_10%,transparent)]'
          : 'border-b border-[var(--border)]'
      }`}
    >
      <span className="w-[74px] shrink-0">
        {variantes.length === 0 ? (
          <Miniatura nome={inicial ?? nome} src={null} className="mx-auto h-11 w-8" />
        ) : (
          <PilhaDeFotos variantes={variantes} nome={nome} altura="h-11" />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{nome}</span>
        {detalhe !== undefined && (
          <span className="block truncate text-xs text-[var(--text-faint)]">{detalhe}</span>
        )}
      </span>

      {contagem !== null && (
        <span className="shrink-0 text-xs text-[var(--text-muted)]">{contagem}</span>
      )}
      {escolhida && (
        <span aria-hidden="true" className="shrink-0 text-[var(--economia)]">
          ✓
        </span>
      )}
    </button>
  )
}
